import { useCallback, useEffect, useRef, useState } from "react";
import { clampFocusBoundary, DEFAULT_FOCUS_BOUNDARY } from "../../../shared/focus";
import type { FocusBoundary, Step, TextCard, TextCardRole, TextCardStyle } from "../../../shared/types";
import { api } from "../../api";
import { setPendingSave, setSaveStatus } from "../dirty";
import { useEditor } from "../DemoEditor";
import { VoiceoverPanel } from "./VoiceoverPanel";

/** Settings for one screen (caption, auto-advance, or text card content). Saves itself, debounced. */
export default function StepSettings({ step }: { step: Step }) {
  const { bundle, setBundle } = useEditor();
  const [caption, setCaption] = useState(step.caption);
  const [textCard, setTextCard] = useState<TextCard | null>(step.textCard ?? null);
  const [chapterTitle, setChapterTitle] = useState(step.chapterTitle ?? "");
  const [auto, setAuto] = useState(step.autoAdvanceMs ? String(step.autoAdvanceMs / 1000) : "");
  const [focus, setFocus] = useState<FocusBoundary | null>(step.focus ?? null);
  const [status, setStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const [err, setErr] = useState<string | null>(null);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inFlight = useRef(0);
  const pendingPayload = useRef<any>(null);

  useEffect(() => {
    setCaption(step.caption);
    setTextCard(step.textCard ?? null);
    setChapterTitle(step.chapterTitle ?? "");
    setAuto(step.autoAdvanceMs ? String(step.autoAdvanceMs / 1000) : "");
    setFocus(step.focus ?? null);
    setStatus("idle");
    setErr(null);
  }, [step.id, step.caption, step.autoAdvanceMs, step.textCard, step.chapterTitle, step.focus]);

  const secs = Number(auto);
  const valid = auto === "" || (secs >= 0.5 && secs <= 120);
  const isVideo = step.mediaType === "video";
  const hasTooltip = step.hotspots.some((h) => h.action === "show_tooltip");

  const flush = useCallback(async () => {
    if (saveTimer.current) {
      clearTimeout(saveTimer.current);
      saveTimer.current = null;
    }
    const p = pendingPayload.current;
    if (!p) return;
    pendingPayload.current = null;
    setPendingSave(false);
    const myId = ++inFlight.current;
    try {
      const b = await api.updateStep(step.id, p);
      if (myId === inFlight.current) {
        setBundle(b);
        setStatus("saved");
        setSaveStatus("saved");
      }
    } catch (e: any) {
      if (myId === inFlight.current) {
        setStatus("error");
        setErr(e.message);
        setSaveStatus("error", e.message);
      }
    }
  }, [step.id, setBundle]);

  // Flush pending changes on unmount
  useEffect(() => () => {
    if (pendingPayload.current) void flush();
  }, [flush]);

  const scheduleSave = (
    nextCaption: string,
    nextAuto: string,
    nextCard: TextCard | null,
    nextChapter?: string,
    nextFocus?: FocusBoundary | null,
  ) => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    const s = Number(nextAuto);
    if (!(nextAuto === "" || (s >= 0.5 && s <= 120))) return;
    const ch = nextChapter !== undefined ? nextChapter : chapterTitle;
    const fo = nextFocus !== undefined ? nextFocus : focus;
    const payload: any = {
      caption: nextCard ? nextCard.title : nextCaption,
      autoAdvanceMs: nextAuto === "" ? null : Math.round(s * 1000),
      chapterTitle: ch.trim() ? ch.trim() : null,
      focus: fo,
    };
    if (nextCard) {
      payload.textCard = nextCard;
      payload.title = nextCard.title;
      payload.subtitle =
        nextCard.role === "intro" ? "Intro screen" : nextCard.role === "outro" ? "Outro screen" : "Text card";
    }
    pendingPayload.current = payload;
    setPendingSave(true);
    setSaveStatus("dirty");
    saveTimer.current = setTimeout(() => void flush(), 1500);
  };

  const toggleFocus = (enabled: boolean) => {
    const next = enabled ? (focus ?? DEFAULT_FOCUS_BOUNDARY) : null;
    setFocus(next);
    scheduleSave(caption, auto, textCard, undefined, next);
  };

  const updateFocus = (patch: Partial<FocusBoundary>) => {
    const base = focus ?? DEFAULT_FOCUS_BOUNDARY;
    const next = clampFocusBoundary({ ...base, ...patch });
    setFocus(next);
    scheduleSave(caption, auto, textCard, undefined, next);
  };

  const updateCard = (patch: Partial<TextCard>) => {
    if (!textCard) return;
    const next = { ...textCard, ...patch };
    setTextCard(next);
    scheduleSave(caption, auto, next);
  };

  return (
    <div className="panel stack-s">
      <h3>{step.textCard ? "Text screen settings" : "Screen settings"}</h3>

      <VoiceoverPanel step={step} />

      {step.textCard && textCard ? (
        <>
          <div className="field">
            <span id="card-role-label">Screen Type</span>
            <div className="action-cards three" role="radiogroup" aria-labelledby="card-role-label">
              {(
                [
                  ["intro", "Intro"],
                  ["outro", "Outro"],
                  ["card", "Message"],
                ] as const
              ).map(([r, label]) => (
                <button
                  key={r}
                  type="button"
                  role="radio"
                  aria-checked={textCard.role === r}
                  className={`action-card ${textCard.role === r ? "on" : ""}`}
                  onClick={() => updateCard({ role: r as TextCardRole })}
                >
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="field">
            <span id="card-style-label">Visual Style</span>
            <div className="action-cards three" role="radiogroup" aria-labelledby="card-style-label">
              {(
                [
                  ["default", "Slate"],
                  ["dark", "Deep Dark"],
                  ["accent", "Brand Glow"],
                ] as const
              ).map(([st, label]) => (
                <button
                  key={st}
                  type="button"
                  role="radio"
                  aria-checked={(textCard.style ?? "default") === st}
                  className={`action-card ${(textCard.style ?? "default") === st ? "on" : ""}`}
                  onClick={() => updateCard({ style: st as TextCardStyle })}
                >
                  <span>{label}</span>
                </button>
              ))}
            </div>
          </div>

          <label className="field">
            <span>Badge / Eyebrow <span className="hint">e.g. "Interactive Tour"</span></span>
            <input
              type="text"
              value={textCard.badge ?? ""}
              maxLength={100}
              placeholder={textCard.role === "intro" ? "Interactive Tour" : textCard.role === "outro" ? "Conclusion" : "Notice"}
              onChange={(e) => updateCard({ badge: e.target.value })}
            />
          </label>

          <label className="field">
            <span>Headline / Title <span className="hint">{"{{variables}}"} work here</span></span>
            <input
              type="text"
              value={textCard.title}
              maxLength={200}
              placeholder="Headline..."
              onChange={(e) => updateCard({ title: e.target.value })}
            />
          </label>

          <label className="field">
            <span>Body text <span className="hint">Multi-line text or paragraphs</span></span>
            <textarea
              value={textCard.body}
              maxLength={1000}
              rows={4}
              placeholder="Descriptive text..."
              onChange={(e) => updateCard({ body: e.target.value })}
            />
          </label>

          <div className="stack-s" style={{ paddingTop: 4 }}>
            <span style={{ fontSize: "var(--fs-s)", fontWeight: 600 }}>Action Buttons</span>
            <div className="row" style={{ gap: 8 }}>
              <label className="field" style={{ flex: 1 }}>
                <span>Primary button</span>
                <input
                  type="text"
                  value={textCard.buttonLabel ?? ""}
                  placeholder={textCard.role === "intro" ? "Start Tour" : textCard.role === "outro" ? "Finish" : "Next"}
                  maxLength={50}
                  onChange={(e) => updateCard({ buttonLabel: e.target.value })}
                />
              </label>
              <label className="field" style={{ flex: 1.5 }}>
                <span>Link URL (optional)</span>
                <input
                  type="url"
                  value={textCard.buttonUrl ?? ""}
                  placeholder="https://..."
                  maxLength={2000}
                  onChange={(e) => updateCard({ buttonUrl: e.target.value })}
                />
              </label>
            </div>

            <div className="row" style={{ gap: 8 }}>
              <label className="field" style={{ flex: 1 }}>
                <span>Secondary button (optional)</span>
                <input
                  type="text"
                  value={textCard.secondaryLabel ?? ""}
                  placeholder="e.g. Restart Tour"
                  maxLength={50}
                  onChange={(e) => updateCard({ secondaryLabel: e.target.value })}
                />
              </label>
              <label className="field" style={{ flex: 1.5 }}>
                <span>Secondary URL (optional)</span>
                <input
                  type="url"
                  value={textCard.secondaryUrl ?? ""}
                  placeholder="https://..."
                  maxLength={2000}
                  onChange={(e) => updateCard({ secondaryUrl: e.target.value })}
                />
              </label>
            </div>
          </div>
        </>
      ) : (
        <label className="field">
          <span>Caption <span className="hint">Shown under the screenshot. {"{{variables}}"} work here</span></span>
          <textarea
            value={caption}
            maxLength={500}
            onChange={(e) => {
              setCaption(e.target.value);
              scheduleSave(e.target.value, auto, null);
            }}
          />
        </label>
      )}

      {isVideo ? (
        <label className="check">
          <input
            type="checkbox"
            checked={auto !== ""}
            onChange={(e) => {
              const v = e.target.checked ? "1" : "";
              setAuto(v);
              scheduleSave(caption, v, textCard);
            }}
          />
          Move to the next screen automatically when the video ends
        </label>
      ) : (
        <label className="field">
          <span>Auto-advance after (seconds) <span className="hint">Leave empty to wait for a click</span></span>
          <input
            type="number"
            min={0.5}
            max={120}
            step={0.5}
            value={auto}
            onChange={(e) => {
              setAuto(e.target.value);
              scheduleSave(caption, e.target.value, textCard);
            }}
          />
        </label>
      )}
      {!valid && <p className="small text-danger">Use 0.5 to 120 seconds.</p>}

      <div className="divider" />
      <div className="stack-s">
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <div className="row" style={{ gap: 8, alignItems: "center" }}>
            <h4 style={{ margin: 0 }}>Pan & Zoom Focus</h4>
          </div>
          <label className="check" style={{ margin: 0 }}>
            <input
              type="checkbox"
              checked={!!focus}
              onChange={(e) => toggleFocus(e.target.checked)}
            />
            <span>Enable</span>
          </label>
        </div>
        <p className="small muted">
          Smoothly zoom the camera into the boxed area when this screen loads in the player. Only what's inside the box is shown.
        </p>

        {focus && (
          <div className="stack-s" style={{ marginTop: 8 }}>


            <div className="grid-2">
              <label className="field">
                <span>Left (X %)</span>
                <input
                  type="number"
                  min={0}
                  max={95}
                  value={focus.x}
                  onChange={(e) => updateFocus({ x: Number(e.target.value) })}
                />
              </label>
              <label className="field">
                <span>Top (Y %)</span>
                <input
                  type="number"
                  min={0}
                  max={95}
                  value={focus.y}
                  onChange={(e) => updateFocus({ y: Number(e.target.value) })}
                />
              </label>
            </div>

            <div className="grid-2">
              <label className="field">
                <span>Crop Width (%)</span>
                <input
                  type="number"
                  min={5}
                  max={100}
                  value={focus.w}
                  onChange={(e) => updateFocus({ w: Number(e.target.value) })}
                />
              </label>
              <label className="field">
                <span>Crop Height (%)</span>
                <input
                  type="number"
                  min={5}
                  max={100}
                  value={focus.h}
                  onChange={(e) => updateFocus({ h: Number(e.target.value) })}
                />
              </label>
            </div>

            <div className="field">
              <span>Zoom Speed</span>
              <div className="tool-group" role="radiogroup" aria-label="Zoom transition speed">
                {([
                  ["slow", "Slow (1.4s)"],
                  ["normal", "Normal (0.8s)"],
                  ["fast", "Fast (0.4s)"],
                ] as const).map(([spd, lbl]) => (
                  <button
                    key={spd}
                    type="button"
                    role="radio"
                    aria-checked={(focus.speed ?? "normal") === spd}
                    className={`tool ${(focus.speed ?? "normal") === spd ? "on" : ""}`}
                    onClick={() => updateFocus({ speed: spd })}
                  >
                    {lbl}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <button
                type="button"
                className="btn btn-sm btn-ghost text-danger"
                onClick={() => toggleFocus(false)}
              >
                Clear Pan & Zoom
              </button>
            </div>
          </div>
        )}
      </div>
      <div className="divider" />

      <div className="field">
        <label>
          <span>Chapter Header <span className="hint">Starts a new section in the tour's Chapters menu</span></span>
          <input
            type="text"
            value={chapterTitle}
            placeholder="e.g. Overview or Deep Dive"
            onChange={(e) => {
              setChapterTitle(e.target.value);
              scheduleSave(caption, auto, textCard, e.target.value);
            }}
          />
        </label>
        {chapterTitle.trim() && (
          <div className="row" style={{ marginTop: 4 }}>
            <span className="small muted">Starts chapter: <b>{chapterTitle.trim()}</b></span>
            <span className="spacer" />
            <button
              type="button"
              className="btn btn-sm btn-ghost"
              onClick={() => {
                setChapterTitle("");
                scheduleSave(caption, auto, textCard, "");
              }}
            >
              Clear chapter
            </button>
          </div>
        )}
      </div>

      <div className="field">
        <span id="transition-label">Transition <span className="hint">Between screens, for the whole demo</span></span>
        <div className="action-cards three" role="radiogroup" aria-labelledby="transition-label">
          {(
            [
              ["none", "Cut"],
              ["fade", "Fade"],
            ] as const
          ).map(([v, label]) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={bundle.demo.transition === v}
              className={`action-card ${bundle.demo.transition === v ? "on" : ""}`}
              onClick={() => {
                if (bundle.demo.transition !== v)
                  api
                    .updateDemo(bundle.demo.id, { transition: v })
                    .then(setBundle)
                    .catch((e) => {
                      setStatus("error");
                      setErr(e.message);
                    });
              }}
            >
              <span>{label}</span>
            </button>
          ))}
        </div>
      </div>

      {!isVideo && auto !== "" && hasTooltip && (
        <p className="small hint">A tooltip hotspot on this screen will hold the timer open, so it won't cut off mid-read.</p>
      )}
      {status === "error" && (
        <p className="small text-danger" role="alert">
          {err}
        </p>
      )}
    </div>
  );
}
